import * as React from "react";
import { useCustom } from "@refinedev/core";
import { SkeletonTable } from "@/components/SkeletonTable";
import { ErrorBanner } from "@/components/ErrorBanner";
import {
  UsageDailyChart,
  UsageKindBreakdown,
  UsagePeriodBar,
  UsageTotalsCards,
} from "@/components/UsageSummary";
import { lastDaysPeriod, usagePeriodQuery } from "@/lib/usage";
import type { AccountUsage, UsagePeriodState } from "@/lib/usage";

/**
 * Spend of one account: all-time total plus the selected window.
 * Backed by GET /admin/usage/:id.
 */
export function UserUsageTab({ userId }: { userId: string }): React.JSX.Element {
  const [period, setPeriod] = React.useState<UsagePeriodState>(() => lastDaysPeriod(30));

  const { query } = useCustom<AccountUsage>({
    url: `/admin/usage/${userId}${usagePeriodQuery(period)}`,
    method: "get",
    queryOptions: { queryKey: ["admin-usage-account", userId, period.from, period.to] },
  });

  const usage = query.data?.data;

  return (
    <div className="py-4">
      <UsagePeriodBar value={period} onChange={setPeriod} />

      {query.isError && (
        <ErrorBanner
          message={
            (query.error as { message?: string } | undefined)?.message ??
            "Не удалось загрузить расходы пользователя"
          }
          onRetry={() => void query.refetch()}
        />
      )}

      {query.isLoading && !usage ? (
        <SkeletonTable rows={4} columns={3} />
      ) : usage ? (
        <>
          <UsageTotalsCards
            period={usage.period}
            allTime={usage.allTime}
            periodState={period}
          />
          <div className="grid grid-cols-1 gap-4">
            <UsageKindBreakdown items={usage.byKind} />
            <UsageDailyChart points={usage.daily} />
          </div>
        </>
      ) : null}
    </div>
  );
}
